import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { searchUsers, type UserOption } from '../../api/create-project'

interface UserPickerProps {
  value: string | null
  onChange: (id: string | null, user: UserOption | null) => void
  excludeIds?: string[]
  placeholder?: string
  disabled?: boolean
}

export function UserPicker({
  value,
  onChange,
  excludeIds = [],
  placeholder = 'جستجو کاربر...',
  disabled,
}: UserPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['user-search', query],
    queryFn: () => searchUsers(query),
    enabled: open,
    staleTime: 30_000,
  })

  const selectedUser = useQuery({
    queryKey: ['user-by-id', value],
    queryFn: async () => {
      if (!value) return null
      const list = await searchUsers('')
      const fromList = list.find((u) => u.id === value)
      if (fromList) return fromList
      return null
    },
    enabled: !!value,
  }).data

  const filtered = users.filter((u) => !excludeIds.includes(u.id))

  if (value && selectedUser) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {selectedUser.full_name ?? selectedUser.email ?? value}
          </p>
          {selectedUser.email && selectedUser.full_name && (
            <p className="truncate text-xs text-muted-foreground">{selectedUser.email}</p>
          )}
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null, null)}
            className="h-7 w-7 p-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pr-9"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {isLoading ? (
            <div className="p-3 text-xs text-muted-foreground">در حال بارگذاری...</div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">کاربری یافت نشد</div>
          ) : (
            filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onChange(u.id, u)
                  setOpen(false)
                  setQuery('')
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-right text-sm hover:bg-accent',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{u.full_name ?? '—'}</p>
                  {u.email && <p className="truncate text-xs text-muted-foreground">{u.email}</p>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
